import React from 'react';
import PropTypes from 'prop-types';
import SelectionBox from './SelectionBox';
import SelectableAreaContext from './SelectableAreaContext';
import {mergeRefs} from '../../lib/refHelper';

const getSelectionRect = (points, offsetTop = 0, offsetLeft = 0, scrollTop = 0, scrollLeft = 0) => {
  const {start, end} = points;

  const x = Math.min(start.x, end.x) + scrollLeft - offsetLeft;
  const y = Math.min(start.y, end.y) + scrollTop - offsetTop;
  const width = Math.abs(start.x - end.x);
  const height = Math.abs(start.y - end.y);

  return {x, y, width, height};
};

const checkIsBoxCollided = (box1, box2) =>
  box1.x < box2.x + box2.width &&
  box1.x + box1.width > box2.x &&
  box1.y < box2.y + box2.height &&
  box1.y + box1.height > box2.y;

class SelectableArea extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      start: {x: 0, y: 0},
      end: {x: 0, y: 0},
    };
    this.initialPoints = {x: 0, y: 0};
    this.mouseDownStatus = false;
    this.isOnProgressSelection = false;
    this.isStartSelecting = false;
    this.registeredItems = new Set();
    this.documentScroll = {
      scrollTop: 0,
      scrollLeft: 0,
    };
    this.initialScroll = {
      scrollTop: 0,
      scrollLeft: 0,
    };
    this.scrollEventListenerExist = false;
    this.selectableAreaRef = null;
    this.selectableAreaRect = {};
    this.disableSelection = false;
    this.contextValue = {
      selectable: {
        register: this.registerSelectable,
        unregister: this.unregisterSelectable,
        setDisableSelection: this.updateDisableSelection,
        disableSelectionOnHover: props.disableSelectionOnHover,
      },
    };
  }

  componentDidMount() {
    const {scrollElementRef} = this.props;
    if (scrollElementRef) {
      scrollElementRef.addEventListener('scroll', this.handleScroll, {passive: true});
      this.scrollEventListenerExist = true;
    }

    document.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('mouseup', this.handleMouseUp);
    document.addEventListener('mousemove', this.handleMouseMove);

    this.updateSelectableAreaRect();
  }

  componentDidUpdate(prevProps) {
    const {scrollElementRef} = this.props;
    if (prevProps.scrollElementRef !== scrollElementRef && scrollElementRef && !this.scrollEventListenerExist) {
      this.handleScroll();
      scrollElementRef.addEventListener('scroll', this.handleScroll, {passive: true});
      this.scrollEventListenerExist = true;
    }
  }

  componentWillUnmount() {
    const {scrollElementRef} = this.props;
    if (scrollElementRef) {
      scrollElementRef.removeEventListener('scroll', this.handleScroll, {passive: true});
    }

    document.removeEventListener('mousedown', this.handleMouseDown);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('mousemove', this.handleMouseMove);
  }

  handleScroll = () => {
    const {scrollElementRef} = this.props;

    this.documentScroll = {
      scrollTop: scrollElementRef.scrollTop,
      scrollLeft: scrollElementRef.scrollLeft,
    };

    this.updateSelectableAreaRect();
    this.updateBoundingRectItems();

    if (this.isOnProgressSelection) {
      const {end} = this.state;

      const endPoint = {...end};

      const startPoint = {
        x: this.initialPoints.x - (this.documentScroll.scrollLeft - this.initialScroll.scrollLeft),
        y: this.initialPoints.y - (this.documentScroll.scrollTop - this.initialScroll.scrollTop),
      };

      this.updateSelectionPointsState(startPoint, endPoint);
    }
  };

  // WARNING: Do not update state in this method! It will make onClick handler on children components not working.
  handleMouseDown = (event) => {
    const isLeftClick = event.button === 0;
    const isInsideSelectableArea = this.selectableAreaRef.contains(event.target);

    if (!isLeftClick || !isInsideSelectableArea || this.disableSelection) return;

    const mousePos = {
      x: event.clientX,
      y: event.clientY,
    };

    this.initialPoints = {...mousePos};
    this.initialScroll = {...this.documentScroll};

    this.mouseDownStatus = true;
    this.isOnProgressSelection = true;
    this.isStartSelecting = true;

    this.updateSelectableAreaRect();
    this.updateBoundingRectItems();
  };

  handleMouseUp = (event) => {
    const isInsideSelectableArea = this.selectableAreaRef.contains(event.target);
    if (this.disableSelection && !this.isOnProgressSelection) return;

    if (!isInsideSelectableArea) return;

    const {onSelectionFinish} = this.props;

    this.mouseDownStatus = false;

    const {start, end} = this.state;

    // get box by mouse position
    const selectionBox = getSelectionRect({start, end});

    const selectedItems = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const item of this.registeredItems) {
      const {boundingRect} = item;
      if (checkIsBoxCollided(boundingRect, selectionBox)) {
        selectedItems.push(item);
      }
    }

    onSelectionFinish(selectedItems);

    this.setState({
      start: {x: 0, y: 0},
      end: {x: 0, y: 0},
    });

    this.isOnProgressSelection = false;
  };

  handleMouseMove = (event) => {
    if (this.disableSelection && !this.isOnProgressSelection) return;

    if (!this.mouseDownStatus) return;

    const endPoint = {
      x: event.clientX,
      y: event.clientY,
    };

    const {start} = this.state;
    let startPoint = start;

    if (this.isStartSelecting) {
      startPoint = {...this.initialPoints};
      this.isStartSelecting = false;
    }

    this.updateSelectionPointsState(startPoint, endPoint);
  };

  updateSelectionPointsState = (startPoint, endPoint) => {
    const {start, end} = this.state;

    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;

    if (startPoint) {
      startX = startPoint.x;
      startY = startPoint.y;
    } else {
      startX = start.x;
      startY = start.y;
    }

    if (endPoint) {
      endX = endPoint.x;
      endY = endPoint.y;

      // Prevent create selection outside the area
      if (endX > this.selectableAreaRect.offsetRight) {
        endX = this.selectableAreaRect.offsetRight - 1.001;
      } else if (endX < this.selectableAreaRect.offsetLeft) {
        endX = this.selectableAreaRect.offsetLeft;
      }
      if (endY > this.selectableAreaRect.offsetBottom) {
        endY = this.selectableAreaRect.offsetBottom - 1.001;
      } else if (endY < this.selectableAreaRect.offsetTop) {
        endY = this.selectableAreaRect.offsetTop;
      }
    } else {
      endX = end.x;
      endY = end.y;
    }

    this.setState({
      start: {
        x: startX,
        y: startY,
      },
      end: {
        x: endX,
        y: endY,
      },
    });
  };

  registerSelectable = (selectableItem) => {
    this.registeredItems.add(selectableItem);
  };

  unregisterSelectable = (selectableItem) => {
    this.registeredItems.delete(selectableItem);
  };

  updateDisableSelection = (isDisable) => {
    this.disableSelection = !!isDisable;
  };

  getSelectableAreaRef = (ref) => {
    this.selectableAreaRef = ref;
  };

  updateSelectableAreaRect = () => {
    if (this.selectableAreaRef) {
      const domRect = this.selectableAreaRef.getBoundingClientRect();

      const {top} = domRect;
      const right = domRect.right - (domRect.width - this.selectableAreaRef.clientWidth);
      const bottom = domRect.bottom - (domRect.height - this.selectableAreaRef.clientHeight);
      const {left} = domRect;

      this.selectableAreaRect = {
        top,
        right,
        bottom,
        left,
        offsetTop: top + this.documentScroll.scrollTop,
        offsetRight: right + this.documentScroll.scrollLeft,
        offsetBottom: bottom + this.documentScroll.scrollTop,
        offsetLeft: left + this.documentScroll.scrollLeft,
        width: domRect.width,
        height: domRect.height,
      };
    }
  };

  updateBoundingRectItems = () => {
    this.registeredItems.forEach((selectableItem) => {
      selectableItem.updateBoundingRect();
    });
  };

  render() {
    const {className, children, disableBlockTextSelect, style, innerRef} = this.props;
    const {start, end} = this.state;

    // call getSelectionRectProps with offsets and scroll to adjust position of absolute position SelectionBox
    const selectionRectProps = getSelectionRect(
      {start, end},
      this.selectableAreaRect?.offsetTop || 0,
      this.selectableAreaRect?.offsetLeft || 0,
      this.documentScroll.scrollTop,
      this.documentScroll.scrollLeft,
    );

    return (
      <SelectableAreaContext.Provider value={this.contextValue}>
        <div
          ref={mergeRefs(innerRef, this.getSelectableAreaRef)}
          className={className || ''}
          style={{
            position: 'relative',
            ...(disableBlockTextSelect ? {userSelect: 'none'} : {}),
            ...style,
          }}
        >
          {children}
          <SelectionBox rect={selectionRectProps} />
        </div>
      </SelectableAreaContext.Provider>
    );
  }
}

SelectableArea.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  style: PropTypes.object,
  disableBlockTextSelect: PropTypes.bool,
  onSelectionFinish: PropTypes.func,
  scrollElementRef: PropTypes.any,
  disableSelectionOnHover: PropTypes.bool,
  innerRef: PropTypes.any,
};

SelectableArea.defaultProps = {
  children: null,
  className: '',
  style: {},
  disableBlockTextSelect: true,
  onSelectionFinish: () => {},
  scrollElementRef: undefined,
  disableSelectionOnHover: false,
  innerRef: null,
};

export default SelectableArea;
